import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const tabs = [
  ["pending", "Pending"],
  ["queued", "Queue"],
  ["played", "Played"],
  ["declined", "Declined"],
  ["expired", "Expired"],
];

const actions = {
  pending: [
    ["queued", "Add to queue"],
    ["declined", "Decline"],
  ],
  queued: [
    ["played", "Mark played"],
    ["declined", "Decline"],
  ],
  expired: [["pending", "Reset to pending"]],
};

function AddSongModal({
  open,
  onClose,
  onSubmit,
  title,
  setTitle,
  artist,
  setArtist,
  busy,
  error,
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="ba-song-dialog"
      aria-labelledby="add-song-heading"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onClose();
        }
      }}
    >
      <div className="ba-song-dialog-body">
        <div className="ba-song-dialog-header">
          <div>
            <h3 id="add-song-heading">Add a song</h3>
            <p>Add a request for this branch.</p>
          </div>

          <button
            type="button"
            className="ba-dialog-close"
            aria-label="Close add song"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit}>
          <fieldset disabled={busy}>
            <label htmlFor="music-title">Song title</label>
            <input
              id="music-title"
              className="form-control"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Enter song title"
              maxLength={200}
              autoFocus
              required
            />

            <label htmlFor="music-artist" className="ba-song-artist-label">
              Artist <span>(optional)</span>
            </label>
            <input
              id="music-artist"
              className="form-control"
              value={artist}
              onChange={(event) => setArtist(event.target.value)}
              placeholder="Enter artist name"
              maxLength={200}
            />

            <div className="ba-dialog-actions">
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                type="submit"
                disabled={busy || !title.trim()}
              >
                {busy ? "Adding…" : "Add song"}
              </button>
            </div>
          </fieldset>
        </form>
      </div>
    </dialog>
  );
}

function headers() {
  return {
    Authorization: localStorage.getItem("jwtToken") || "",
  };
}

function TimeLimitModal({
  open,
  onClose,
  value,
  onChange,
  onSave,
  saving,
  error,
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="ba-time-dialog"
      aria-labelledby="time-limit-heading"
      onCancel={(event) => {
        event.preventDefault();
        if (!saving) onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && !saving) {
          onClose();
        }
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSave();
        }}
      >
        <div className="ba-time-dialog-header">
          <h3 id="time-limit-heading">Request time limit</h3>

          <button
            type="button"
            className="ba-dialog-close"
            aria-label="Close time limit"
            disabled={saving}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <p>
          Applies to new and reset requests. Existing deadlines stay unchanged.
        </p>

        <label htmlFor="music-expiry-minutes">Minutes</label>

        <input
          id="music-expiry-minutes"
          className="form-control"
          type="number"
          min="0"
          max="525600"
          step="1"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={saving}
          autoFocus
          required
        />

        <small>Set 0 for no time limit.</small>

        {error && (
          <div className="alert alert-danger mt-3" role="alert">
            {error}
          </div>
        )}

        <div className="ba-dialog-actions">
          <button
            type="button"
            className="btn btn-outline-light"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function SongRequester({ user }) {
  const [imageFailed, setImageFailed] = useState(false);

  const requester = user && typeof user === "object" ? user : null;

  const name = requester?.nickname || requester?.name || "Unknown member";

  const rawImage = String(requester?.img_url || "").trim();

  const imageUrl = rawImage
    ? /^https?:\/\//i.test(rawImage)
      ? rawImage
      : `https://bealyo-upload-files.s3.us-east-2.amazonaws.com/${rawImage.replace(/^\/+/, "")}`
    : "";

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  return (
    <div className="ba-song-requester">
      {imageUrl && !imageFailed ? (
        <img
          className="ba-song-requester-avatar"
          src={imageUrl}
          alt=""
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span
          className="ba-song-requester-avatar ba-song-requester-fallback"
          aria-hidden="true"
        >
          {Array.from(name.trim())[0] || "?"}
        </span>
      )}

      <span className="ba-song-requester-name" dir="auto">
        {name}
      </span>
    </div>
  );
}

export default function AdminMusic({ circleId }) {
  const [status, setStatus] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [revision, setRevision] = useState(0);
  const [musicEnabled, setMusicEnabled] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showAddSong, setShowAddSong] = useState(false);

  const [expiryMinutes, setExpiryMinutes] = useState("60");
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [now, setNow] = useState(Date.now());

  const [showTimeLimit, setShowTimeLimit] = useState(false);
  const [savedExpiryMinutes, setSavedExpiryMinutes] = useState(60);
  const [expiryError, setExpiryError] = useState("");
  const lastMusicViewRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const base =
    process.env.REACT_APP_API_URL + `circles/admin/${circleId}/music`;

  useEffect(() => {
    if (busy || showAddSong || showTimeLimit) return;

    const timer = setInterval(() => {
      setRevision((value) => value + 1);
    }, 15000);

    return () => clearInterval(timer);
  }, [base, busy, showAddSong, showTimeLimit]);

  useEffect(() => {
    let cancelled = false;

    const viewKey = `${base}:${status}`;
    const isNewView = lastMusicViewRef.current !== viewKey;

    lastMusicViewRef.current = viewKey;

    // Show the loading screen only when opening a Circle or switching tabs.
    if (isNewView) {
      setLoading(true);
      setRequests([]);
    }

    setError("");

    axios
      .get(base, {
        headers: headers(),
        params: { status },
      })
      .then(({ data }) => {
        if (!cancelled) {
          setRequests(data.requests);
          setMusicEnabled(data.musicRequestsEnabled === true);
          const minutes = data.musicRequestExpiryMinutes ?? 60;

          setExpiryMinutes(String(minutes));
          setSavedExpiryMinutes(minutes);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || "Unable to load songs.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [base, status, revision]);

  useEffect(() => {
    if (!message) return;

    const timeout = setTimeout(() => setMessage(""), 3000);

    return () => clearTimeout(timeout);
  }, [message]);

  const addSong = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");

    try {
      await axios.post(
        base,
        { title: title.trim(), artist: artist.trim() },
        { headers: headers() },
      );

      setTitle("");
      setArtist("");
      setStatus("pending");
      setRevision((value) => value + 1);
      setMessage("Song added to Pending.");
      setShowAddSong(false);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to add song.");
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (requestId, nextStatus) => {
    setBusy(true);
    setError("");
    setMessage("");

    try {
      await axios.patch(
        `${base}/${requestId}/status`,
        { status: nextStatus },
        { headers: headers() },
      );

      setRevision((value) => value + 1);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to update song.");
    } finally {
      setBusy(false);
    }
  };

  const toggleMusicRequests = async () => {
    setSavingSettings(true);
    setError("");
    setMessage("");

    try {
      const { data } = await axios.patch(
        `${base}/settings`,
        { musicRequestsEnabled: !musicEnabled },
        { headers: headers() },
      );

      setMusicEnabled(data.musicRequestsEnabled);
      setMessage(
        data.musicRequestsEnabled
          ? "Song requests enabled."
          : "Song requests disabled.",
      );
    } catch (err) {
      setError(
        err.response?.data?.message || "Unable to update music settings.",
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const saveExpirySettings = async () => {
    if (savingExpiry) return;

    const minutes = Number(expiryMinutes);

    if (
      expiryMinutes.trim() === "" ||
      !Number.isSafeInteger(minutes) ||
      minutes < 0 ||
      minutes > 525600
    ) {
      setExpiryError("Enter whole minutes between 0 and 525600.");
      return;
    }

    setSavingExpiry(true);
    setExpiryError("");
    setMessage("");

    try {
      const { data } = await axios.patch(
        `${base}/settings`,
        { musicRequestExpiryMinutes: minutes },
        { headers: headers() },
      );

      setSavedExpiryMinutes(data.musicRequestExpiryMinutes);
      setExpiryMinutes(String(data.musicRequestExpiryMinutes));
      setShowTimeLimit(false);

      setMessage(
        data.musicRequestExpiryMinutes === 0
          ? "Request time limit disabled."
          : `Request time limit saved: ${data.musicRequestExpiryMinutes} minutes.`,
      );
    } catch (err) {
      setExpiryError(
        err.response?.data?.message || "Unable to save request time limit.",
      );
    } finally {
      setSavingExpiry(false);
    }
  };

  const requestTimeLabel = (request) => {
    if (request.status === "expired") return "Expired";

    if (!["pending", "queued"].includes(request.status)) return null;

    if (!request.expiresAt) {
      return request.requestedAt ? "No time limit" : "No deadline recorded";
    }

    const deadline = new Date(request.expiresAt).getTime();

    if (!Number.isFinite(deadline)) return "Invalid deadline";

    const secondsLeft = Math.max(0, Math.ceil((deadline - now) / 1000));

    if (secondsLeft === 0) return "Time limit reached";

    const hours = Math.floor(secondsLeft / 3600);
    const minutes = Math.floor((secondsLeft % 3600) / 60);
    const seconds = secondsLeft % 60;

    const mm = String(minutes).padStart(2, "0");
    const ss = String(seconds).padStart(2, "0");

    return hours > 0
      ? `${hours}:${mm}:${ss} remaining`
      : `${mm}:${ss} remaining`;
  };

  const requestDisplayDate = (request) =>
    request.status === "played"
      ? request.playedAt
      : request.requestedAt || request.createdAt;

  return (
    <section className="ba-panel ba-music">
      <div className="ba-music-header">
        <div>
          <h3>Music requests</h3>
          <p>Manage requests and shape your branch’s soundtrack.</p>
        </div>

        <div className="ba-music-tools">
          <div className="ba-music-setting">
            <span>Member requests</span>

            <button
              type="button"
              role="switch"
              aria-label="Allow members to request songs"
              aria-checked={musicEnabled}
              className={
                musicEnabled ? "btn btn-success" : "btn btn-outline-light"
              }
              disabled={loading || savingSettings || savingExpiry || busy}
              onClick={toggleMusicRequests}
            >
              {savingSettings
                ? "Saving…"
                : musicEnabled
                  ? "Enabled"
                  : "Disabled"}
            </button>
          </div>

          <button
            type="button"
            className="ba-time-limit-link"
            aria-haspopup="dialog"
            disabled={loading || busy || savingSettings || savingExpiry}
            onClick={() => {
              setExpiryMinutes(String(savedExpiryMinutes));
              setExpiryError("");
              setShowTimeLimit(true);
            }}
          >
            <span aria-hidden="true">◷</span>
            Time limit:{" "}
            {savedExpiryMinutes === 0
              ? "No limit"
              : `${savedExpiryMinutes} min`}
          </button>

          <button
            type="button"
            className="ba-add-song-link"
            disabled={busy || savingSettings || savingExpiry}
            onClick={() => {
              setError("");
              setMessage("");
              setShowAddSong(true);
            }}
          >
            + Add song
          </button>
        </div>
      </div>

      {error && !showAddSong && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {message && (
        <div className="alert alert-success" role="status">
          {message}
        </div>
      )}

      <TimeLimitModal
        open={showTimeLimit}
        onClose={() => {
          if (!savingExpiry) setShowTimeLimit(false);
        }}
        value={expiryMinutes}
        onChange={setExpiryMinutes}
        onSave={saveExpirySettings}
        saving={savingExpiry}
        error={expiryError}
      />

      <AddSongModal
        open={showAddSong}
        onClose={() => setShowAddSong(false)}
        onSubmit={addSong}
        title={title}
        setTitle={setTitle}
        artist={artist}
        setArtist={setArtist}
        busy={busy}
        error={error}
      />

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginTop: 30,
          marginBottom: 20,
        }}
      >
        {tabs.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={
              status === value ? "btn btn-primary" : "btn btn-outline-light"
            }
            aria-pressed={status === value}
            disabled={busy}
            onClick={() => {
              setStatus(value);
              setMessage("");
            }}
          >
            {label}
          </button>
        ))}

        <button
          type="button"
          className="btn btn-outline-light"
          disabled={loading || busy}
          onClick={() => setRevision((value) => value + 1)}
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <p role="status">Loading songs…</p>
      ) : error ? null : requests.length === 0 ? (
        <div className="ba-music-empty">
          <span aria-hidden="true">♫</span>
          <strong>No songs here yet</strong>
          <p>Requests will appear here when they’re added.</p>
        </div>
      ) : (
        <div className="ba-song-list">
          <div className="ba-song-list-heading">
            <span>Song / Artist</span>
            <span>Requested by</span>
            <span>
              {status === "played" ? "Played at" : "Requested / Time left"}
            </span>
            <span className="ba-song-actions-heading">Actions</span>
          </div>

          {requests.map((request, index) => (
            <div className="ba-song-row" key={request._id}>
              <div className="ba-song-identity">
                <span className="ba-song-number" aria-hidden="true">
                  {status === "queued" ? index + 1 : "♫"}
                </span>

                <div className="ba-song-details">
                  <strong dir="auto">{request.title}</strong>
                  <span dir="auto">
                    {request.artist || "Artist not specified"}
                  </span>
                </div>
              </div>
              <SongRequester user={request.requestedBy} />
              <time
                className="ba-song-date"
                dateTime={requestDisplayDate(request)}
                title={new Date(
                  request.requestedAt || request.createdAt,
                ).toLocaleString()}
              >
                {new Date(requestDisplayDate(request)).toLocaleTimeString(
                  undefined,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )}

                <span>
                  {new Date(
                    request.requestedAt || request.createdAt,
                  ).toLocaleTimeString(undefined, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>

                {requestTimeLabel(request) && (
                  <span
                    className={`ba-song-expiry ${
                      request.status === "expired" ||
                      (request.expiresAt &&
                        new Date(request.expiresAt).getTime() <= now)
                        ? "ba-song-expiry-ended"
                        : ""
                    }`}
                    title={
                      request.expiresAt
                        ? `Deadline: ${new Date(request.expiresAt).toLocaleString()}`
                        : undefined
                    }
                  >
                    {requestTimeLabel(request)}
                  </span>
                )}
              </time>

              <div className="ba-song-actions">
                <a
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(
                    [request.title, request.artist].filter(Boolean).join(" "),
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn ba-youtube-button"
                  aria-label={`Search YouTube for ${request.title}${
                    request.artist ? ` by ${request.artist}` : ""
                  } (opens in a new tab)`}
                >
                  YouTube ↗
                </a>
                {(actions[status] || []).map(([nextStatus, label]) => (
                  <button
                    key={nextStatus}
                    type="button"
                    className={
                      nextStatus === "declined"
                        ? "btn btn-outline-danger"
                        : "btn btn-primary"
                    }
                    disabled={busy || savingSettings}
                    onClick={() => changeStatus(request._id, nextStatus)}
                    aria-label={`${label}: ${request.title}`}
                  >
                    {label}
                  </button>
                ))}

                {!actions[status] && (
                  <span className={`ba-song-status ba-song-status-${status}`}>
                    {status === "played" ? "Played" : "Declined"}
                  </span>
                )}
              </div>
            </div>
          ))}

          <div className="ba-song-list-footer">
            {requests.length} {requests.length === 1 ? "song" : "songs"} shown
            {requests.length === 100 ? " · Display limited to 100" : ""}
          </div>
        </div>
      )}
    </section>
  );
}
